import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';
import { Alert } from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import { Bio } from '../../data/constants';

// ---------- STYLED COMPONENTS ----------

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  @media (max-width: 960px) {
    padding: 0px;
  }
`;

const SocialMediaIcons = styled.div`
  display: flex;
  margin-top: 1rem;
  justify-content: center;
`;

const SocialMediaIcon = styled.a`
  background: ${({ theme }) => theme.card};
  border: 2px solid ${({ theme }) => theme.primary};
  border-radius: 50%;
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ theme }) => theme.primary};
  transition: all 0.3s ease-in-out;
  margin-right: 15px;
  box-shadow: 0 2px 8px ${({ theme }) => theme.shadow};
  
  &:hover {
    background: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.white};
    box-shadow: 0 4px 16px ${({ theme }) => theme.shadow};
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1350px;
  padding: 0px 0px 80px 0px;
  gap: 12px;
`;

const Title = styled.div`
  font-size: 42px;
  text-align: center;
  font-weight: 600;
  margin-top: 20px;
  color: ${({ theme }) => theme.text_primary};
  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 32px;
  }
`;

const Desc = styled.div`
  font-size: 18px;
  text-align: center;
  max-width: 600px;
  color: ${({ theme }) => theme.text_secondary};
  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 16px;
  }
`;

const ContactForm = styled.form`
  width: 100%;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.card};
  padding: 32px;
  border-radius: 16px;
  box-shadow: rgba(23, 92, 230, 0.15) 0px 4px 24px;
  margin-top: 28px;
  gap: 16px;
`;

const Row = styled.div`
  display: flex;
  width: 100%;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FormRight = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ContactInput = styled.input`
  flex: 1;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.text_secondary};
  outline: none;
  font-size: 18px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 10px;
  padding: 12px 16px;
  &:focus {
    border: 1px solid ${({ theme }) => theme.primary};
  }
`;

const ContactInputMessage = styled.textarea`
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.text_secondary};
  outline: none;
  font-size: 18px;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 10px;
  padding: 12px 16px;
  &:focus {
    border: 1px solid ${({ theme }) => theme.primary};
  }
`;

const ContactButton = styled.input`
  width: 100%;
  text-align: center;
  background: linear-gradient(
    225deg,
    hsla(271, 100%, 50%, 1) 0%,
    hsla(294, 100%, 50%, 1) 100%
  );
  padding: 13px 16px;
  margin-top: 8px;
  border-radius: 12px;
  border: none;
  color: ${({ theme }) => theme.text_primary};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
`;

const ContactDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px 16px;
  background-color: ${({ theme }) => theme.background};
  border-radius: 12px;
  font-size: 16px;
  color: ${({ theme }) => theme.text_primary};
`;

const DetailTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  cursor: pointer;
  &::after {
    content: "";
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 30px;
    height: 3px;
    background: linear-gradient(
      225deg,
      hsla(271, 100%, 50%, 1) 0%,
      hsla(294, 100%, 50%, 1) 100%
    );
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 20%;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;  
  color: ${({ theme }) => theme.text_primary + 95};
`;

// ---------- COMPONENT ----------

const Contact = () => {
  const form = useRef();
  const [alertInfo, setAlertInfo] = useState({ show: false, severity: 'success', message: '' });

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(form.current);
    const email = formData.get("from_email");

    if (!isValidEmail(email)) {
      setAlertInfo({ show: true, severity: 'error', message: 'Please enter a valid email address' });
      return;
    }

    emailjs.sendForm(
      'service_wlo9xv6',
      'template_1wmsyeo',
      form.current,
      'Zravr0wLSKGBwGLJt'
    ).then(
      () => {
        setAlertInfo({ show: true, severity: 'success', message: 'Message sent successfully!' });
        form.current.reset();
      },
      (error) => {
        setAlertInfo({ show: true, severity: 'error', message: 'Error sending message, try again!' });
        console.log(error.text);
      }
    );
  };

  return (
    <Container id="contact">
      <Wrapper>
        <Title>Contact</Title>
        <Desc>Have You Any Project? Please Drop a Message</Desc>

        {/* Alert MUI affiché en haut */}
        {alertInfo.show && (
          <Alert 
            severity={alertInfo.severity} 
            sx={{ width: { xs: '100%', sm: '50%' }, marginBottom: 2 }} 
            onClose={() => setAlertInfo({ ...alertInfo, show: false })}
          >
            {alertInfo.message}
          </Alert>
        )}

        <ContactForm ref={form} onSubmit={handleSubmit}>
          <Row>
            <FormRight>
              <ContactInput placeholder="Your Email" name="from_email" />
              <ContactInput placeholder="Your Name" name="from_name" />
              <ContactInput placeholder="Subject" name="subject" />
              <ContactInputMessage placeholder="Message" rows="5" name="message" />
              <ContactButton type="submit" value="Send 🚀" />
            </FormRight>

            <ContactDetails>
              <Desc>
                I am available on almost every social media. You can message me, I will reply within 24 hours.
              </Desc>

              <DetailTitle>Address</DetailTitle>
              <DetailItem>📍 Casablanca, Morocco</DetailItem>

              <DetailTitle>Phone</DetailTitle>
              <DetailItem>📱 +212 618 030 413</DetailItem>

              <SocialMediaIcons>
                <SocialMediaIcon href={Bio.linkedin} target="display"><LinkedInIcon /></SocialMediaIcon>
                <SocialMediaIcon href={`mailto:${Bio.gmail}`} target="display"><EmailIcon /></SocialMediaIcon>
                <SocialMediaIcon href={Bio.facebook} target="display"><FacebookIcon /></SocialMediaIcon>
                <SocialMediaIcon href={Bio.insta} target="display"><InstagramIcon /></SocialMediaIcon>
              </SocialMediaIcons>
            </ContactDetails>
          </Row>
        </ContactForm>
      </Wrapper>
    </Container>
  );
};

export default Contact;
